import { useQuery } from '@tanstack/react-query';

import {
  payCalendarsService,
  companiesService,
  individualsService,
  FetchIndividualDeductionsReportsRequest,
  individualDeductionsReportsService,
  useToast,
} from '@aslanpay/portal-common-lib';

import { IndividualDeductionsReportsPage } from 'app/components/UI/Table/Table.types';

import { getPayCycleName } from 'app/utils/strings';

const useFetchIndividualDeductionsReportsPage = (
  {
    ids,
    companyIds,
    individualIds,
    payCalendarIds,
    payCycleIds,
    status,
    query,
    sortBy,
    sort,
    page,
    size,
  }: FetchIndividualDeductionsReportsRequest,
  enabled = true
) => {
  const toast = useToast();

  return useQuery<IndividualDeductionsReportsPage, Error>(
    [
      'fetchIndividualDeductionsReportsPage',
      ids,
      companyIds,
      individualIds,
      payCalendarIds,
      payCycleIds,
      status,
      query,
      sortBy,
      sort,
      page,
      size,
    ],
    async () => {
      const reports = await individualDeductionsReportsService.fetchIndividualDeductionsReports({
        ids,
        companyIds,
        individualIds,
        payCalendarIds,
        payCycleIds,
        status,
        query,
        sortBy,
        sort,
        page,
        size,
      });

      const companies = await companiesService.fetchCompanies({
        ids: reports.list.map((report) => report.companyId),
        page: 0,
        size: 999999,
      });

      const filteredPayCalendarIds = Array.from(
        new Set(reports.list.map((report) => report.payCalendarId))
      ).filter(Boolean);

      const payCalendars = await payCalendarsService.fetchPayCalendars({
        ids: filteredPayCalendarIds,
        page: 0,
        size: 999999,
      });

      const employees = await individualsService.fetchIndividuals({
        ids: reports.list.map((report) => report.individualId),
        role: ['EMPLOYEE'],
        page: 0,
        size: 999999,
      });

      const reportsList = reports.list.map((report) => {
        const companyName =
          companies.list.find((company) => company.id === report.companyId)?.name || '';

        const payCalendar = payCalendars.list.find(
          (calendar) => calendar.id === report.payCalendarId
        );

        const payCycle = payCalendar?.payCycles.find((cycle) => cycle.id === report.payCycleId);

        const payCycleName = getPayCycleName(payCycle, payCalendar?.payFrequency || 'MONTHLY');

        const employee = employees.list.find((emp) => emp.id === report.individualId);
        const employeeName = employee
          ? `${employee.firstName} ${employee.lastName}`
          : 'Deleted User';

        return {
          ...report,
          companyName,
          payCycleName,
          employeeName,
        };
      });

      return { ...reports, list: reportsList };
    },
    {
      enabled,
      onError: () => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: `Sorry, we failed to fetch the individual reports`,
        });
      },
    }
  );
};

export default useFetchIndividualDeductionsReportsPage;
